import React, { FC, memo, useMemo } from 'react';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import {
  Box,
  parseDiscountData,
  useGetBoxDiscountedPriceString,
  useGetBoxRentalPriceString,
  useGetBoxRentalPriceStringWithVat,
} from 'entities/Box';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { RentOptionDocument } from 'entities/RentOption';
import { getLoggedUserData } from 'entities/User';
import { WarehouseDiscount } from 'entities/Warehouse';
import { trimPriceFrequency } from 'features/SizeCodeCollapse/utils/helpers/trimPriceFrequency';
import { getWarehouseInfo } from 'pages/BookingPage';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
import { Popup } from 'shared/ui/Popup';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';

interface BookingMapDiscountBannerProps {
  selectedBox: Box;
  rentOptions: RentOptionDocument[] | undefined;
}

export const BookingMapDiscountBanner: FC<BookingMapDiscountBannerProps> = memo(({ selectedBox, rentOptions }) => {
  const { t } = useAppTranslation('booking');

  const discountRentOptions = useMemo(() => {
    const opts = rentOptions ?? [];
    const square = selectedBox.sizeCode.square;
    const rentDiscounts = opts.filter((option) => option.unitSizes?.includes(square.toString()));
    const generalDiscounts = opts.filter((option) => !option.unitSizes && option.showAsPromotion);

    return [...generalDiscounts, ...rentDiscounts];
  }, [rentOptions, selectedBox.sizeCode.square]);

  if (discountRentOptions.length === 0) {
    return null;
  }

  return (
    <div className="bg-accent text-white px-4 py-2 text-center">
      <div className="flex flex-wrap justify-center items-center gap-x-1 gap-y-1 desktop:gap-2">
        <span className="whitespace-nowrap text-xs desktop:text-sm">{t('This size is discounted the')}</span>
        <div className="flex flex-wrap justify-center gap-x-1 gap-y-1 desktop:gap-2">
          {discountRentOptions.map((discount, index) => {
            const isLast = index === discountRentOptions.length - 1;

            return (
              <div key={`${discount.label}-${index}`} className="flex items-center gap-1">
                <span className="text-xs desktop:text-sm">{`"${getLocalizedString(discount.label)}"`}</span>
                {discount?.extendedDescription ? (
                  <Popup
                    description={
                      <div
                        className="max-w-[310px] desktop:max-w-[600px]"
                        dangerouslySetInnerHTML={{ __html: getLocalizedString(discount.extendedDescription) }}
                      />
                    }
                  >
                    <InfoIcon width={18} height={18} className="[&>path]:stroke-white cursor-pointer flex-shrink-0" />
                  </Popup>
                ) : null}
                {!isLast ? <span className="text-xs desktop:text-sm">,</span> : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

interface BookingMapPriceBlockProps {
  selectedBox: Box | undefined;
}

export const BookingMapPriceBlock: FC<BookingMapPriceBlockProps> = memo(({ selectedBox }) => {
  const { t } = useAppTranslation('booking');
  const { isDesktop } = useScreenBreakpoints();
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);
  const currencySymbol = useGetCurrencySymbol();
  const showPriceWithVat = globalSettings?.showPriceWithVat;

  const discountedPrice = useGetBoxDiscountedPriceString({
    warehouseInfo,
    boxInfo: selectedBox ?? null,
    showPriceWithVat,
    accountType: loggedUser?.accountType,
  });

  const discountedPriceText = discountedPrice?.split(currencySymbol);

  const firstMonthDiscount = useMemo((): WarehouseDiscount | undefined => {
    if (!warehouseInfo?.warehouseDiscounts || !selectedBox) {
      return undefined;
    }

    const sortedDiscounts = [...warehouseInfo.warehouseDiscounts].sort((a, b) => a.priority - b.priority);

    return sortedDiscounts?.find((discount) => {
      const hasUnitSizes = Boolean(discount.unitSizes?.length);
      const includesFirstMonth = discount.discounts.split(',').some((d) => FIRST_MONTH_DISCOUNT_REGEX.test(d.trim()));

      if (hasUnitSizes) {
        const unitSizes = discount.unitSizes.split(',').map((size) => Number(size));

        return includesFirstMonth && unitSizes?.includes(selectedBox.sizeCode.square);
      }

      return includesFirstMonth;
    });
  }, [selectedBox, warehouseInfo?.warehouseDiscounts]);

  const parsedDiscount = useMemo(() => parseDiscountData(firstMonthDiscount), [firstMonthDiscount]);

  const rentalBoxPriceWithVat = useGetBoxRentalPriceStringWithVat(
    selectedBox ?? null,
    InvoiceFrequencyType.MONTH,
    warehouseInfo,
    loggedUser?.accountType,
  );

  const rentalBoxPrice = useGetBoxRentalPriceString(selectedBox ?? null);
  const rentalPrice = showPriceWithVat ? rentalBoxPriceWithVat : rentalBoxPrice;

  if (!selectedBox) {
    return null;
  }

  if (!isDesktop) {
    return (
      <div className="flex flex-col items-start gap-0.5 min-w-0">
        <div className="flex flex-row flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {discountedPrice ? (
            <>
              <span className="text-lg font-bold text-primary whitespace-nowrap leading-none">
                {trimPriceFrequency(`${discountedPriceText?.[0]} ${currencySymbol}/${t('Month')}`)}
              </span>
              {discountedPriceText?.[1] && (
                <span className="text-xs text-primary leading-none whitespace-nowrap">{discountedPriceText[1].trim()}</span>
              )}
              {(parsedDiscount?.description || discountedPriceText?.[1]) && (
                <Popup description={<div className="max-w-[310px]">{parsedDiscount?.description}</div>}>
                  <InfoIcon width={18} height={18} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0" />
                </Popup>
              )}
            </>
          ) : (
            <span className="text-lg font-bold text-primary whitespace-nowrap leading-none">{trimPriceFrequency(rentalPrice)}</span>
          )}
        </div>
        {discountedPrice && rentalPrice ? (
          <span className="text-error text-sm line-through leading-none whitespace-nowrap">{trimPriceFrequency(rentalPrice)}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end text-right w-full min-w-0 max-w-full">
      <div className="flex flex-wrap justify-end items-center gap-x-1 w-full text-base text-primary font-semibold desktop:text-lg wideDesktop:text-xl">
        {discountedPrice ? (
          <>
            <div className="flex items-center justify-end gap-1 text-base desktop:text-lg wideDesktop:text-xl font-semibold text-right">
              {`${discountedPriceText?.[0]} ${currencySymbol}/${t('Month')}`}

              {!discountedPriceText?.[1] && parsedDiscount?.description ? (
                <Popup description={<div className="max-w-[310px] desktop:max-w-[600px]">{parsedDiscount.description}</div>}>
                  <InfoIcon width={20} height={20} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0 mb-2" />
                </Popup>
              ) : null}
            </div>
            {discountedPriceText?.[1] ? (
              <div className="flex items-center justify-end">
                <span className="text-xs desktop:text-sm desktop:p-1">{discountedPriceText[1]}</span>
                {parsedDiscount?.description ? (
                  <Popup description={<div className="max-w-[310px] desktop:max-w-[600px]">{parsedDiscount.description}</div>}>
                    <InfoIcon width={20} height={20} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0 mb-2" />
                  </Popup>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          rentalPrice
        )}
      </div>
      {discountedPrice && rentalPrice ? (
        <div className="w-full text-error text-sm desktop:text-base line-through text-right">{rentalPrice}</div>
      ) : null}
    </div>
  );
});
